import { toast } from 'react-toastify';
import ApiServices from '../../../../../../platform/app/src/shared/ApiServices/ApiServices';
import { getQueryParam } from "../../../../../../platform/app/src/utils/queryParams";

/**
 * Processes form inputs in a cloned DOM element to preserve their current values
 */
export const processFormInputs = (clonedElement: HTMLElement): void => {
    // Handle input elements
    clonedElement.querySelectorAll('input').forEach(input => {
        if (input.type === 'checkbox' || input.type === 'radio') {
            if (input.checked) {
                input.setAttribute('checked', 'true');
            } else {
                input.removeAttribute('checked');
            }
        } else {
            input.setAttribute('value', input.value);
        }
    });

    // Handle textarea elements
    clonedElement.querySelectorAll('textarea').forEach(textarea => {
        textarea.innerHTML = textarea.value;
    });

    // Handle select elements
    clonedElement.querySelectorAll('select').forEach(select => {
        select.querySelectorAll('option').forEach(option => {
            if (option.value === select.value) {
                option.setAttribute('selected', 'true');
            } else {
                option.removeAttribute('selected');
            }
        });
    });
};

/**
 * Creates a complete HTML document from the container content
 */
export const createHtmlDocument = (containerContent: string): string => {
    return `<!DOCTYPE html><html lang="en"><head></head><body>${containerContent}</body></html>`;
};

/**
 * Uploads report content to S3 using pre-assigned URL
 */
export const uploadReportToS3 = async (htmlContent: string, studyId: string): Promise<void> => {
    const preAssignedData = await ApiServices.getPreAssignedUrl(studyId);
    const {
        url,
        fields: {
            AWSAccessKeyId,
            policy,
            signature,
            key,
            'x-amz-meta-custom-header': metaHeader,
            'x-amz-security-token': xAmzSecurityToken,
        },
    } = preAssignedData;

    const formData = new FormData();
    formData.append('Content-Type', 'text/html');
    formData.append('key', key);
    formData.append('AWSAccessKeyId', AWSAccessKeyId);
    formData.append('x-amz-security-token', xAmzSecurityToken);
    formData.append('policy', policy);
    formData.append('signature', signature);
    formData.append('x-amz-meta-custom-header', metaHeader);

    const file = new File([htmlContent], 'report.html', { type: 'text/html' });
    formData.append('file', file);

    await ApiServices.uploadReport(url, formData);
};

/**
 * Main function to save report content from a DOM container
 */
export const saveReport = async (
    container: HTMLElement,
    options: {
        studyId?: string;
        successMessage?: string;
        errorMessage?: string;
        onSuccess?: () => void;
        onError?: (error: any) => void;
    } = {}
): Promise<void> => {
    const {
        studyId = getQueryParam('studyId'),
        successMessage = 'Report saved successfully!',
        errorMessage = 'Failed to save report',
        onSuccess,
        onError
    } = options;

    if (!studyId) {
        throw new Error('Study ID is required');
    }

    try {
        // Clone the container and process form inputs
        const cloned = container.cloneNode(true) as HTMLElement;
        processFormInputs(cloned);

        // Create full HTML document
        const fullHtmlContent = createHtmlDocument(cloned.innerHTML);

        // Upload to S3
        await uploadReportToS3(fullHtmlContent, studyId);

        // Show success message
        toast.success(successMessage);

        // Close popup if applicable
        setTimeout(() => {
            if (window.name === 'popupWindow') {
                window.close();
            }
        }, 2000);

        // Call success callback
        onSuccess?.();

    } catch (error) {
        toast.error(errorMessage);
        console.error('Report save error:', error);
        onError?.(error);
        throw error;
    }
};
